// A route's elevation profile: distance along the bottom, height up the side,
// the elevation thresholds as reference lines, and the suggested forecast
// points as markers — numbered to match the list beneath it, filled when chosen
// (or already added) and hollow when not, so the state is carried by shape as
// well as colour. Clicking a marker toggles it, the same as its checkbox.
//
// Plain SVG, no chart library. One series, so no legend box; the list below is
// the table view, so the tooltip only ever repeats what's written there.
import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";

// Validated with the dataviz skill's palette checker against the white paper
// surface (slot-1 blue: lightness band, chroma and 3:1 contrast all pass).
const SERIES = "#2a78d6";
const SURFACE = "#ffffff";
const GRID = "#ececec";
const THRESHOLD = "#b0b0b0";
const INK_2 = "rgba(0,0,0,0.6)";   // text.secondary
const INK_3 = "rgba(0,0,0,0.45)";

const HEIGHT = 220;
const M = { top: 22, right: 16, bottom: 30, left: 48 };

// Clean tick steps for the axes.
function niceStep(range, target) {
  const raw = range / Math.max(1, target);
  const mag = 10 ** Math.floor(Math.log10(raw));
  const n = raw / mag;
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * mag;
}
const ticks = (lo, hi, step) => {
  const out = [];
  for (let v = Math.ceil(lo / step) * step; v <= hi + 1e-9; v += step) out.push(Math.round(v * 1000) / 1000);
  return out;
};

/**
 * Props:
 *  - profile:     [[km, elevation|null], …]
 *  - thresholds:  [500, 750, 1000] — drawn only where they fall inside the range
 *  - markers:     [{ key, n, km, elevation, name, on }]
 *  - onToggle:    (key) => void, or absent for a read-only chart
 */
export default function ElevationProfile({ profile, thresholds = [], markers = [], onToggle }) {
  const wrap = useRef(null);
  const [width, setWidth] = useState(640);
  const [hover, setHover] = useState(null);   // index into `pts`

  useEffect(() => {
    const el = wrap.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(([e]) => setWidth(Math.max(280, Math.round(e.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const pts = useMemo(() => (profile || []).filter(p => p[1] !== null && p[1] !== undefined), [profile]);

  const geom = useMemo(() => {
    if (pts.length < 2) return null;
    const maxKm = pts[pts.length - 1][0] || 1;
    const eles = pts.map(p => p[1]);
    const lo = Math.max(0, Math.floor((Math.min(...eles) - 50) / 100) * 100);
    const hi = Math.ceil((Math.max(...eles) + 50) / 100) * 100;
    const iw = width - M.left - M.right;
    const ih = HEIGHT - M.top - M.bottom;
    const x = (km) => M.left + (km / maxKm) * iw;
    const y = (e) => M.top + ih - ((e - lo) / (hi - lo)) * ih;
    const line = pts.map((p, i) => `${i ? "L" : "M"}${x(p[0]).toFixed(1)},${y(p[1]).toFixed(1)}`).join("");
    const area = `${line}L${x(pts[pts.length - 1][0]).toFixed(1)},${y(lo)}L${x(pts[0][0]).toFixed(1)},${y(lo)}Z`;
    return { maxKm, lo, hi, iw, ih, x, y, line, area,
             xTicks: ticks(0, maxKm, niceStep(maxKm, Math.max(3, Math.floor(iw / 80)))),
             yTicks: ticks(lo, hi, niceStep(hi - lo, 4)) };
  }, [pts, width]);

  if (!geom) {
    return (
      <Typography variant="body2" color="text.secondary">
        No elevation profile — the GPX file has no heights.
      </Typography>
    );
  }
  const { x, y, lo, hi, maxKm } = geom;

  // The crosshair snaps to the nearest profile sample to the pointer's x.
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const km = ((e.clientX - r.left - M.left) / geom.iw) * maxKm;
    if (km < 0 || km > maxKm) { setHover(null); return; }
    let best = 0;
    for (let i = 1; i < pts.length; i++) if (Math.abs(pts[i][0] - km) < Math.abs(pts[best][0] - km)) best = i;
    setHover(best);
  };
  const h = hover !== null ? pts[hover] : null;
  const near = h ? markers.find(m => Math.abs(m.km - h[0]) <= maxKm / 100) : null;
  const tipLeft = h ? Math.min(Math.max(x(h[0]) - 70, 0), width - 150) : 0;

  return (
    <Box ref={wrap} sx={{ position: "relative", width: "100%" }}>
      <svg width={width} height={HEIGHT} role="img"
           aria-label={`Elevation profile, ${Math.round(maxKm)} km, from ${Math.round(Math.min(...pts.map(p => p[1])))} to ${Math.round(Math.max(...pts.map(p => p[1])))} m`}
           onPointerMove={onMove} onPointerLeave={() => setHover(null)}
           style={{ display: "block", fontFamily: "inherit" }}>
        {/* Grid and axes: hairline, solid, recessive. */}
        {geom.yTicks.map(t => (
          <g key={`y${t}`}>
            <line x1={M.left} x2={width - M.right} y1={y(t)} y2={y(t)} stroke={GRID} strokeWidth="1" />
            <text x={M.left - 6} y={y(t) + 4} textAnchor="end" fontSize="11" fill={INK_2}
                  style={{ fontVariantNumeric: "tabular-nums" }}>{t.toLocaleString("en-GB")}</text>
          </g>
        ))}
        {geom.xTicks.map(t => (
          <text key={`x${t}`} x={x(t)} y={HEIGHT - M.bottom + 16} textAnchor="middle" fontSize="11" fill={INK_2}
                style={{ fontVariantNumeric: "tabular-nums" }}>{t}</text>
        ))}
        <text x={width - M.right} y={HEIGHT - 4} textAnchor="end" fontSize="11" fill={INK_3}>km</text>
        <text x={M.left - 6} y={M.top - 8} textAnchor="end" fontSize="11" fill={INK_3}>m</text>

        {/* Elevation thresholds, labelled directly in text ink. */}
        {thresholds.filter(t => t > lo && t < hi).map(t => (
          <g key={`t${t}`}>
            <line x1={M.left} x2={width - M.right} y1={y(t)} y2={y(t)} stroke={THRESHOLD} strokeWidth="1" />
            <text x={width - M.right - 2} y={y(t) - 4} textAnchor="end" fontSize="11" fill={INK_2}>{t} m</text>
          </g>
        ))}

        <path d={geom.area} fill={SERIES} fillOpacity="0.1" />
        <path d={geom.line} fill="none" stroke={SERIES} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />

        {h && (
          <line x1={x(h[0])} x2={x(h[0])} y1={M.top} y2={M.top + geom.ih} stroke={INK_3} strokeWidth="1" />
        )}

        {/* Markers: >=8px with a 2px surface ring, a 24px hit area, keyboard
            reachable. Filled = chosen or already added; hollow = not. */}
        {markers.map(m => {
          if (m.elevation === null || m.elevation === undefined) return null;
          const cx = x(m.km);
          const cy = y(Math.min(Math.max(m.elevation, lo), hi));
          const act = onToggle && !m.added ? () => onToggle(m.key) : undefined;
          return (
            <g key={m.key} tabIndex={act ? 0 : -1} role={act ? "button" : undefined}
               aria-label={`${m.n}. ${m.name}, km ${m.km.toFixed(1)}, ${m.added ? "added" : m.on ? "chosen" : "not chosen"}`}
               aria-pressed={act ? m.on : undefined}
               onClick={act}
               onKeyDown={act ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); act(); } } : undefined}
               style={{ cursor: act ? "pointer" : "default", outline: "none" }}>
              <circle cx={cx} cy={cy} r="12" fill="transparent" />
              <circle cx={cx} cy={cy} r="7" fill={SURFACE} />
              <circle cx={cx} cy={cy} r="5" fill={m.on || m.added ? SERIES : SURFACE}
                      stroke={SERIES} strokeWidth="2" />
              <text x={cx} y={cy - 11} textAnchor="middle" fontSize="10" fontWeight="600" fill={INK_2}>{m.n}</text>
            </g>
          );
        })}
      </svg>

      {h && (
        <Box sx={{ position: "absolute", top: 0, left: tipLeft, pointerEvents: "none",
                   bgcolor: "background.paper", border: "1px solid", borderColor: "divider",
                   borderRadius: 1, px: 1, py: 0.5, boxShadow: 1, minWidth: 110 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{Math.round(h[1]).toLocaleString("en-GB")} m</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>km {h[0].toFixed(1)}</Typography>
          {near && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              {near.n}. {near.name}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
